// Reporting WIDGETS — the instance model, the viz-type catalogue and the token
// encode/decode. PURE, and a LEAF module (imports nothing from the app's module
// graph, only its own types).
//
// ⚠️ LEAF ON PURPOSE, for exactly the reason googleAdsChartCatalog.ts documents.
// BOTH dashboards' persistence modules (chartPrefs.ts on Meta,
// googleAdsViewPrefs.ts on Google) import from here to decode their stored view
// state. If this module imported a COMPONENT — and the viz-type registry is the
// obvious place that would creep in — a production bundle that happened to
// initialise the component first could leave a constant here `undefined` at the
// point a prefs module reads it, which is the class of bug that white-screened
// the app once already and that a green `npm run build` did not catch.
//
// So the split is deliberate: WHAT viz types exist lives here (a leaf, safe for
// prefs to import); WHICH COMPONENT renders each one lives beside the components
// in src/modules/reporting/ReportingWidgets.tsx, which nothing in the
// persistence path imports.
//
// ── WHAT A WIDGET IS ───────────────────────────────────────────────────────
// A user-added instance: `{ id, vizType, metricKey, showComparison }`. The
// dashboard supplies the METRIC CATALOGUE (Meta's CHART_METRICS, Google's
// GOOGLE_ADS_METRICS), so one framework serves both without either dashboard's
// vocabulary leaking in here — `decodeWidgets` takes the "is this a metric I
// know?" predicate rather than a metric list.
//
// The shape is designed to GROW: Phase 2's dimensional types (pie, ranked bar,
// funnel, gauge) add a `dimension` and a top-N, which slot in as extra token
// segments without disturbing the four Phase-1 ones.
//
// ── PERSISTENCE ────────────────────────────────────────────────────────────
// One token per instance, in the SAME `User.gridPreferences` envelope every
// other reporting view-state token rides in:
//
//     wgt:<id>:<vizType>:<metricKey>:<flags>
//
// and the widget's position in the grid rides the region-aware layout core as
// `lay:widget:<id>:<x>:<y>:<w>:<h>` — parallel to `lay:tile:` / `lay:chart:` /
// `lay:table:`, so the coming save model can treat every one of them uniformly.
//
// Nothing here throws. A malformed token, an unknown viz type or a metric this
// dashboard doesn't have all DROP that widget — the same degrade-to-sane
// posture as chartLayout / googleAdsViewPrefs, and the only honest one: a widget
// pointing at a metric we cannot resolve has nothing to render, and rendering it
// as some *other* metric would silently show the user a number they never asked
// for.

/**
 * The registered representations.
 *
 * Phase 1: singleNumber + trend (both off summary/trend data).
 * Phase 2: funnel + gauge (also summary-only), then the DIMENSIONAL pair —
 * rankedBar + pie — which split one metric by a dimension the dashboard already
 * aggregates.
 */
export type WidgetVizType = 'singleNumber' | 'trend' | 'funnel' | 'gauge' | 'rankedBar' | 'pie';

export interface WidgetVizTypeDef {
  key: WidgetVizType;
  label: string;
  hint: string;
  /**
   * Whether the vs-previous-period comparison means anything for this
   * representation.
   *
   * ⚠️ TRUE FOR THE TREND TOO. The first cut said no — "a trend already shows
   * the movement" — but a trend widget leads with a VALUE HEADER, exactly as a
   * KPI tile pairs its mini-chart with a value and a delta. The chart shows the
   * shape within the period; the delta answers a different question (how this
   * period compares to the last one), which the shape cannot.
   */
  supportsComparison: boolean;
  /**
   * Whether the user picks the metric. FALSE for the funnel, whose three stages
   * are fixed (impressions → clicks → conversions) — offering a metric selector
   * there would imply a choice that does not exist.
   */
  needsMetric: boolean;
  /**
   * Whether this type splits its metric BY something. Only the dimensional pair
   * (rankedBar, pie) do; they also take a top-N.
   */
  needsDimension: boolean;
  /** Only a percentage metric can honestly fill a 0–100 dial. */
  percentOnly?: boolean;
}

/** Catalogue order = the order the config popover offers them. */
export const WIDGET_VIZ_TYPES: WidgetVizTypeDef[] = [
  {
    key: 'singleNumber',
    label: 'Single number',
    hint: 'One large figure for the period, optionally with the change vs the previous one',
    supportsComparison: true,
    needsMetric: true,
    needsDimension: false,
  },
  {
    key: 'trend',
    label: 'Trend',
    hint: 'The value plus its shape over time — line, bar or area',
    supportsComparison: true,
    needsMetric: true,
    needsDimension: false,
  },
  {
    key: 'funnel',
    label: 'Funnel',
    // ⚠️ NO metric selector: the three stages ARE the funnel. Letting someone
    // pick "CTR" for a funnel would produce a shape that means nothing.
    hint: 'Impressions → clicks → conversions, and the drop-off between them',
    supportsComparison: false,
    needsMetric: false,
    needsDimension: false,
  },
  {
    key: 'gauge',
    label: 'Gauge',
    hint: 'One percentage on a 0–100% dial — impression share, CTR, conversion rate',
    supportsComparison: true,
    needsMetric: true,
    needsDimension: false,
    percentOnly: true,
  },
  {
    key: 'rankedBar',
    label: 'Ranked bar',
    hint: 'The top values of a dimension by a metric — “top 10 locations by spend”',
    supportsComparison: false,
    needsMetric: true,
    needsDimension: true,
  },
  {
    key: 'pie',
    label: 'Pie / donut',
    hint: 'One metric split by a dimension, top values plus an “Other” slice',
    supportsComparison: false,
    needsMetric: true,
    needsDimension: true,
  },
];

/**
 * Sensible list length for the dimensional types, and the bounds a stored one is
 * held to.
 *
 * ⚠️ THE CEILING IS 300, THE DEFAULT IS STILL 8. The ceiling exists so a ranked
 * bar can be a whole-roster league table ("every location by spend") rather than
 * a top-20 that quietly omits the rest; the DEFAULT stays small because that is
 * what a panel-sized chart reads well at, and raising it would change every
 * existing widget. A 300-row bar is far taller than any panel, so the renderer
 * SCROLLS INTERNALLY — see RankedBarWidget, where the `min-h-0` on both the
 * widget root and its scroll region is what keeps the list inside the panel
 * instead of blowing out the canvas.
 *
 * A pie shares these bounds but has no use for the top of the range — 300 slices
 * is not a readable pie. That is a judgement the user makes per widget, not a
 * second constant: the two types share one "top N" control and splitting it
 * would mean two numbers to reason about for one idea.
 */
export const DEFAULT_WIDGET_TOP_N = 8;
export const MIN_WIDGET_TOP_N = 3;
export const MAX_WIDGET_TOP_N = 300;

export function clampTopN(value: number | undefined): number {
  if (value === undefined || !Number.isFinite(value)) return DEFAULT_WIDGET_TOP_N;
  return Math.min(MAX_WIDGET_TOP_N, Math.max(MIN_WIDGET_TOP_N, Math.round(value)));
}

export const DEFAULT_WIDGET_VIZ_TYPE: WidgetVizType = 'singleNumber';

const VIZ_KEYS = new Set<WidgetVizType>(WIDGET_VIZ_TYPES.map((v) => v.key));

export function isWidgetVizType(value: string): value is WidgetVizType {
  return VIZ_KEYS.has(value as WidgetVizType);
}

export function widgetVizTypeDef(key: WidgetVizType): WidgetVizTypeDef | undefined {
  return WIDGET_VIZ_TYPES.find((v) => v.key === key);
}

/** One user-added widget instance. */
export interface ReportingWidget {
  /** Stable, token-safe id — also this widget's panel key in the grid region. */
  id: string;
  vizType: WidgetVizType;
  /** A key from the HOST dashboard's metric catalogue. Ignored by the funnel. */
  metricKey: string;
  /** Show the change vs the previous equivalent period. */
  showComparison: boolean;
  /**
   * What a DIMENSIONAL widget splits by — a key from the host dashboard's own
   * dimension list. Absent on the non-dimensional types, and absent on a
   * dimensional one that has not been configured yet (the renderer then falls
   * back to the dashboard's first dimension rather than showing nothing).
   */
  dimension?: string;
  /** How many values a dimensional widget shows before "Other". */
  topN?: number;
}

const TOKEN_PREFIX = 'wgt:';
const COMPARE_ON = 'c';
const COMPARE_OFF = '-';

/**
 * ⚠️ Ids are restricted to token-safe characters, and a token carrying anything
 * else is dropped. The id is a grid PANEL KEY and a token SEGMENT, so a hand-
 * edited id containing a colon would make its own token unparseable and could
 * collide with a neighbour's — cheaper to refuse than to escape.
 */
const ID_PATTERN = /^[A-Za-z0-9_-]+$/;

export function isWidgetId(value: string): boolean {
  return ID_PATTERN.test(value);
}

/**
 * The smallest unused `w<n>` id.
 *
 * Deterministic rather than random: the id ends up in a stored token and in the
 * layout token beside it, so a readable, reproducible id makes a stored
 * preference legible and the tests exact. Reusing a removed widget's id is safe
 * because a remove is itself a save, and encoding prunes the layout tokens to
 * the widgets that currently exist.
 */
export function newWidgetId(existing: readonly ReportingWidget[]): string {
  const taken = new Set(existing.map((w) => w.id));
  // n+1 candidates against at most n taken ids — one is always free.
  for (let n = 1; n <= existing.length + 1; n += 1) {
    const id = `w${n}`;
    if (!taken.has(id)) return id;
  }
  /* c8 ignore next */
  return `w${existing.length + 1}`;
}

/**
 * A new widget: a single number of the given metric, comparing to the previous
 * period. That default is the point of a scorecard — a bare figure with nothing
 * to measure it against is the least useful thing this can show — and it is one
 * click to turn off.
 */
export function createWidget(
  existing: readonly ReportingWidget[],
  metricKey: string,
): ReportingWidget {
  return {
    id: newWidgetId(existing),
    vizType: DEFAULT_WIDGET_VIZ_TYPE,
    metricKey,
    showComparison: true,
  };
}

/** Replace one widget's config, leaving order and every other widget untouched. */
export function updateWidget(
  widgets: readonly ReportingWidget[],
  id: string,
  patch: Partial<Omit<ReportingWidget, 'id'>>,
): ReportingWidget[] {
  return widgets.map((w) => (w.id === id ? { ...w, ...patch } : w));
}

export function removeWidget(
  widgets: readonly ReportingWidget[],
  id: string,
): ReportingWidget[] {
  return widgets.filter((w) => w.id !== id);
}

/**
 * The widget region's panel-key catalogue — used to prune the stored layout, so
 * a removed widget can't leave a `lay:widget:` token accumulating forever.
 */
export function widgetPanelKeys(widgets: readonly ReportingWidget[]): string[] {
  return widgets.map((w) => w.id);
}

/** Stands in for an absent optional segment, so positions stay fixed. */
const NONE = '-';

/**
 * Encode the instances as `wgt:` tokens, in render order.
 *
 * ⚠️ THE PHASE-2 FIELDS ARE APPENDED, so a Phase-1 four-field token still
 * decodes and nobody loses a widget they built. A widget with no dimension and
 * the default top-N emits the SHORT form, so adding these fields did not churn
 * every stored token.
 */
export function encodeWidgets(widgets: readonly ReportingWidget[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const w of widgets) {
    if (!w.id || !isWidgetId(w.id) || seen.has(w.id)) continue;
    if (!isWidgetVizType(w.vizType)) continue;
    if (!w.metricKey) continue;
    seen.add(w.id);
    const head = `${TOKEN_PREFIX}${w.id}:${w.vizType}:${w.metricKey}:${
      w.showComparison ? COMPARE_ON : COMPARE_OFF
    }`;
    const dimension = w.dimension && isTokenSegment(w.dimension) ? w.dimension : NONE;
    const topN = w.topN === undefined ? NONE : String(clampTopN(w.topN));
    out.push(dimension === NONE && topN === NONE ? head : `${head}:${dimension}:${topN}`);
  }
  return out;
}

/** A value safe to sit in one `:`-delimited segment. */
function isTokenSegment(value: string): boolean {
  return value.length > 0 && !value.includes(':');
}

/**
 * Decode `wgt:` tokens back into instances. Every other view-state token sharing
 * the preset is ignored.
 *
 * ⚠️ AN UNRESOLVABLE WIDGET IS DROPPED, NOT DEFAULTED. An unknown viz type or a
 * metric outside `isKnownMetric` means we cannot render what the user configured
 * — and substituting a default would put a DIFFERENT metric, or a different
 * representation, on screen under the arrangement they built. Dropping it is the
 * only outcome that never shows a figure nobody asked for. A legacy/empty token
 * list decodes to no widgets, which is also the fresh-user state.
 */
export function decodeWidgets(
  tokens: readonly string[],
  isKnownMetric: (metricKey: string) => boolean,
  isKnownDimension: (dimension: string) => boolean = () => true,
): ReportingWidget[] {
  const seen = new Set<string>();
  const out: ReportingWidget[] = [];
  for (const token of tokens) {
    if (!token.startsWith(TOKEN_PREFIX)) continue;
    const parts = token.slice(TOKEN_PREFIX.length).split(':');
    // id : vizType : metricKey : flags [ : dimension : topN ]
    //
    // ⚠️ FIXED POSITIONS FROM THE LEFT. Phase 1 took the metric key from the
    // MIDDLE so a future colon-bearing key would still resolve — but that is
    // irreconcilable with OPTIONAL trailing fields (a six-segment token would be
    // ambiguous between "colon in the metric key" and "dimension + topN"). No
    // metric key in either catalogue contains a colon, so the speculative
    // tolerance was traded for a real one: Phase-1 tokens still decode
    // unchanged, and nobody loses a widget they built.
    if (parts.length < 4) continue;
    const [id, vizType, metricKey, flags, dimensionRaw, topNRaw] = parts as [
      string,
      string,
      string,
      string,
      string | undefined,
      string | undefined,
    ];
    if (!isWidgetId(id) || seen.has(id)) continue;
    if (!isWidgetVizType(vizType)) continue;
    if (!metricKey || !isKnownMetric(metricKey)) continue;

    // ⚠️ A dimension this dashboard does not have DROPS the widget, exactly as an
    // unknown metric does — the realistic case being a Google token read by Meta.
    // Splitting by some OTHER dimension would put a chart on screen that answers
    // a question nobody asked. An ABSENT dimension is not an unknown one: the
    // widget is simply unconfigured and the renderer falls back.
    let dimension: string | undefined;
    if (dimensionRaw !== undefined && dimensionRaw !== NONE && dimensionRaw !== '') {
      if (!isKnownDimension(dimensionRaw)) continue;
      dimension = dimensionRaw;
    }

    let topN: number | undefined;
    if (topNRaw !== undefined && topNRaw !== NONE && topNRaw !== '') {
      const parsed = Number(topNRaw);
      // Garbage falls back to the default rather than dropping the widget — an
      // unreadable list LENGTH is a cosmetic problem, unlike an unreadable
      // dimension, which would change what the chart is about.
      topN = clampTopN(Number.isFinite(parsed) ? parsed : undefined);
    }

    seen.add(id);
    out.push({
      id,
      vizType,
      metricKey,
      showComparison: flags.includes(COMPARE_ON),
      ...(dimension !== undefined ? { dimension } : {}),
      ...(topN !== undefined ? { topN } : {}),
    });
  }
  return out;
}
